import React, { useState, useEffect } from 'react';
import { X, Type, Palette, Image as ImageIcon, Move, Sparkles, Eye, Globe, Wrench, ChevronDown, Crop, LayoutGrid } from 'lucide-react';
import { blockLabels, blockIcons } from '../content-editor/blockTypes';
import UniversalContentEditor from '../content-editor/UniversalContentEditor';
import { BlockStyleControls } from '../content-editor/DashboardParts';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import FloatingImageToolbar from './imageFrameSystem/FloatingImageToolbar';

/**
 * EditDrawer — Iter 150 redesign.
 *
 * Slide-out drawer that replaces the always-visible 400px panel. Houses
 * an accordion with ONLY ONE section expanded at a time to reduce
 * cognitive load. Modeled after Squarespace's settings drawer.
 *
 * Accordions:
 *   • Content       — block fields (text, headlines, images, items)
 *   • Design        — palette, font, font color
 *   • Background    — bg image, bg color
 *   • Spacing       — section height, image size, attached
 *   • Animation     — entrance animation + delay/speed
 *   • Visibility    — is_visible toggle
 *   • SEO           — anchor id (for in-page navigation)
 *   • Advanced      — custom CSS class
 */
const ACCORDIONS = [
  { id: 'content',    label: 'Content',     icon: Type       },
  { id: 'layout',     label: 'Layout',      icon: LayoutGrid },
  { id: 'design',     label: 'Design',      icon: Palette    },
  { id: 'background', label: 'Background',  icon: ImageIcon  },
  { id: 'spacing',    label: 'Spacing',     icon: Move       },
  { id: 'animation',  label: 'Animation',   icon: Sparkles   },
  { id: 'visibility', label: 'Visibility',  icon: Eye        },
  { id: 'seo',        label: 'SEO',         icon: Globe      },
  { id: 'advanced',   label: 'Advanced',    icon: Wrench     },
];

const HEIGHTS = [
  { v: 'compact',   label: 'Compact'    },
  { v: 'normal',    label: 'Normal'     },
  { v: 'tall',      label: 'Tall'       },
  { v: 'fullbleed', label: 'Full Bleed' },
];

const ANIMATIONS = [
  { v: 'none',    label: 'None'        },
  { v: 'fade',    label: 'Fade In'     },
  { v: 'slide-up',label: 'Slide Up'    },
  { v: 'slide-left', label: 'Slide Left' },
  { v: 'zoom',    label: 'Zoom'        },
];

function Accordion({ item, open, onToggle, children }) {
  const Icon = item.icon;
  return (
    <div className="border-b border-white/10" data-testid={`accordion-${item.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-white/[0.03] transition-all"
        data-testid={`accordion-toggle-${item.id}`}
      >
        <span className="flex items-center gap-2.5">
          <Icon size={13} className={open ? 'text-[#A78BFA]' : 'text-[#9CA3AF]'} />
          <span className={`text-[12px] uppercase tracking-[0.14em] font-bold ${open ? 'text-white' : 'text-[#CBD5E1]'}`}>
            {item.label}
          </span>
        </span>
        <ChevronDown size={13} className={`text-[#94A3B8] transition-transform ${open ? 'rotate-180 text-[#A78BFA]' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1" data-testid={`accordion-body-${item.id}`}>
          {children}
        </div>
      )}
    </div>
  );
}

export default function EditDrawer({ block, onClose, onUpdate, onMediaPick, renderContentEditor, imageEditing }) {
  // Iter 178h — when `imageEditing` is provided (from a double-click on an
  // image), surface an "Image" accordion at the top with the
  // FloatingImageToolbar rendered inline. Open by default so the user lands
  // straight on the image editing tools instead of seeing two overlays.
  const [openId, setOpenId] = useState(imageEditing ? 'image' : 'content');
  useEffect(() => { if (imageEditing) setOpenId('image'); }, [imageEditing]);
  if (!block) return null;
  const c = block.content || {};
  const s = c._style || {};
  const Icon = blockIcons[block.type];

  const setMeta = (k, v) => onUpdate(block.id, k, v);
  const setStyle = (k, v) => onUpdate(block.id, '_style', { ...s, [k]: v });

  const ACCORDIONS_NOW = imageEditing
    ? [{ id: 'image', label: 'Image', icon: Crop }, ...ACCORDIONS]
    : ACCORDIONS;

  return (
    <div
      className="fixed top-0 right-0 w-[380px] h-full z-[140] bg-[#11112B] border-l border-white/10 shadow-[-12px_0_40px_-12px_rgba(0,0,0,0.5)] flex flex-col"
      data-testid="edit-panel"
      data-wb-overlay="1"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-gradient-to-b from-[#1A1A3E] to-[#11112B]">
        <div className="flex items-center gap-2">
          {Icon ? <Icon size={14} className="text-[#A78BFA]" /> : null}
          <span className="text-white font-bold text-[13px] tracking-wide">
            Edit {blockLabels[block.type] || block.type}
          </span>
        </div>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onClose?.(); }}
          onClick={(e) => { e.stopPropagation(); onClose?.(); }}
          className="text-[#9CA3AF] hover:text-white p-1 rounded-md hover:bg-white/5 transition-all"
          data-testid="edit-drawer-close"
          aria-label="Close"
        >
          <X size={15} />
        </button>
      </div>

      {/* Accordion list */}
      <div className="flex-1 overflow-y-auto">
        {ACCORDIONS_NOW.map((acc) => (
          <Accordion
            key={acc.id}
            item={acc}
            open={openId === acc.id}
            onToggle={() => setOpenId(openId === acc.id ? null : acc.id)}
          >
            {acc.id === 'image' && imageEditing && (
              <div className="space-y-3" data-testid="image-edit-accordion">
                <p className="text-[11px] text-[#9CA3AF] leading-snug">
                  Editing the image · double-click any image to swap or crop.
                </p>
                <FloatingImageToolbar
                  inline
                  blockId={imageEditing.blockId}
                  field={imageEditing.field}
                  imageUrl={imageEditing.imageUrl}
                  frame={imageEditing.frame}
                  onUpdateFrame={imageEditing.onUpdateFrame}
                  onReplace={imageEditing.onReplace}
                  onMediaPick={imageEditing.onMediaPick}
                  onRemove={imageEditing.onRemove}
                  onClose={imageEditing.onClose}
                />
              </div>
            )}

            {acc.id === 'content' && (
              renderContentEditor
                ? renderContentEditor(block)
                : <UniversalContentEditor block={block} onChange={(next) => onUpdate(block.id, '__replace_content__', next)} onMediaPick={onMediaPick} hideSectionMeta />
            )}

            {acc.id === 'design' && (
              <DesignBlock block={block} onUpdate={onUpdate} />
            )}

            {acc.id === 'layout' && (
              <LayoutBlock block={block} onUpdate={onUpdate} />
            )}

            {acc.id === 'background' && (
              <BackgroundBlock block={block} setStyle={setStyle} />
            )}

            {acc.id === 'spacing' && (
              <SpacingBlock block={block} setMeta={setMeta} setStyle={setStyle} />
            )}

            {acc.id === 'animation' && (
              <AnimationBlock style={s} setStyle={setStyle} />
            )}

            {acc.id === 'visibility' && (
              <VisibilityBlock block={block} onUpdate={onUpdate} />
            )}

            {acc.id === 'seo' && (
              <SeoBlock c={c} setMeta={setMeta} />
            )}

            {acc.id === 'advanced' && (
              <AdvancedBlock c={c} setMeta={setMeta} />
            )}
          </Accordion>
        ))}
      </div>
    </div>
  );
}

// ── Sub-blocks (inline; could be extracted later) ──────────────────────

const COLOR_PRESETS = ['#FFFFFF', '#F8FAFC', '#FAF6EE', '#FDF2F8', '#0E0E23', '#0F172A', '#1F2937', '#8B5CF6', '#F97316'];

function DesignBlock({ block, onUpdate }) {
  return (
    <div className="space-y-4">
      <BlockStyleControls block={block} onUpdate={onUpdate} />
    </div>
  );
}

// ── Layout — Squarespace/Wix-style container width, padding, aspect, bg
function LayoutBlock({ block, onUpdate }) {
  const layout = block.layout || {};
  const set = (patch) => onUpdate(block.id, '_layout', { ...layout, ...patch });

  const CONTAINERS = [
    { v: 'narrow',      label: 'Narrow',      hint: '720px · long-form text' },
    { v: 'content',     label: 'Content',     hint: '1140px · standard' },
    { v: 'wide',        label: 'Wide',        hint: '1440px · galleries' },
    { v: 'full-bleed',  label: 'Full-bleed',  hint: '100vw · edge-to-edge' },
  ];
  const PADDINGS = [
    { v: 'compact',   label: 'Compact',    hint: '48/32' },
    { v: 'normal',    label: 'Normal',     hint: '88/56' },
    { v: 'editorial', label: 'Editorial',  hint: '140/80' },
    { v: 'cinematic', label: 'Cinematic',  hint: '200/120' },
  ];
  const ASPECTS = [
    { v: 'auto', label: 'Auto' },
    { v: '1:1',  label: '1:1' },
    { v: '4:5',  label: '4:5' },
    { v: '3:4',  label: '3:4' },
    { v: '16:9', label: '16:9' },
    { v: '21:9', label: '21:9' },
  ];
  const BG_OPTIONS = [
    { v: 'auto',   label: 'Auto',   swatch: 'linear-gradient(135deg,#FAF6EE,#FFFFFF)' },
    { v: 'light',  label: 'Light',  swatch: '#FFFFFF' },
    { v: 'cream',  label: 'Cream',  swatch: '#FAF6EE' },
    { v: 'ivory',  label: 'Ivory',  swatch: '#F5F0E8' },
    { v: 'tinted', label: 'Tinted', swatch: '#F8FAFC' },
    { v: 'noir',   label: 'Noir',   swatch: '#0E0E23' },
    { v: 'accent', label: 'Accent', swatch: '#8B5CF6' },
  ];

  const supportsMediaBleed = ['hero', 'single_image', 'gallery', 'banner'].includes(block.type);

  return (
    <div className="space-y-5" data-testid="layout-block">
      {/* Container width */}
      <div>
        <Label className="text-[#9CA3AF] text-[10.5px] uppercase tracking-wider font-bold block mb-2">Container width</Label>
        <div className="grid grid-cols-2 gap-1.5">
          {CONTAINERS.map((o) => (
            <button
              key={o.v}
              onClick={() => set({ container_width: o.v })}
              className={`text-left px-2.5 py-2 rounded-md text-[10.5px] uppercase tracking-wider font-bold border transition-all ${
                (layout.container_width || 'content') === o.v
                  ? 'bg-[#A78BFA]/15 border-[#A78BFA]/50 text-[#A78BFA]'
                  : 'bg-white/[0.02] border-white/10 text-[#9CA3AF] hover:text-white'
              }`}
              data-testid={`layout-width-${o.v}`}
            >
              {o.label}
              <span className="block text-[9px] normal-case tracking-normal opacity-70 mt-0.5">{o.hint}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Section padding */}
      <div>
        <Label className="text-[#9CA3AF] text-[10.5px] uppercase tracking-wider font-bold block mb-2">Section padding</Label>
        <div className="grid grid-cols-2 gap-1.5">
          {PADDINGS.map((o) => (
            <button
              key={o.v}
              onClick={() => set({ padding_scale: o.v })}
              className={`text-left px-2.5 py-2 rounded-md text-[10.5px] uppercase tracking-wider font-bold border transition-all ${
                (layout.padding_scale || 'editorial') === o.v
                  ? 'bg-[#A78BFA]/15 border-[#A78BFA]/50 text-[#A78BFA]'
                  : 'bg-white/[0.02] border-white/10 text-[#9CA3AF] hover:text-white'
              }`}
              data-testid={`layout-padding-${o.v}`}
            >
              {o.label}
              <span className="block text-[9px] normal-case tracking-normal opacity-70 mt-0.5">{o.hint}px</span>
            </button>
          ))}
        </div>
      </div>

      {/* Background variant */}
      <div>
        <Label className="text-[#9CA3AF] text-[10.5px] uppercase tracking-wider font-bold block mb-2">Background</Label>
        <div className="grid grid-cols-4 gap-1.5">
          {BG_OPTIONS.map((o) => (
            <button
              key={o.v}
              onClick={() => set({ background_variant: o.v })}
              className={`px-1.5 py-2 rounded-md text-[9px] uppercase tracking-wider font-bold border transition-all ${
                (layout.background_variant || 'auto') === o.v
                  ? 'ring-2 ring-[#A78BFA] border-transparent text-white'
                  : 'border-white/10 text-[#9CA3AF] hover:text-white'
              }`}
              data-testid={`layout-bg-${o.v}`}
            >
              <span className="block w-full h-4 rounded mb-1" style={{ background: o.swatch }} />
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* Full-bleed media toggle — hero, image, gallery, banner */}
      {supportsMediaBleed && (
        <div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={layout.full_bleed_media !== false && (block.type === 'hero' ? true : !!layout.full_bleed_media)}
              onChange={(e) => set({ full_bleed_media: e.target.checked })}
              className="mt-1 w-4 h-4 accent-[#A78BFA]"
              data-testid="layout-fullbleed-media"
            />
            <div>
              <p className="text-white text-[12px] font-bold">Full-bleed media</p>
              <p className="text-[#9CA3AF] text-[10.5px]">Images or background run edge-to-edge (100vw)</p>
            </div>
          </label>
        </div>
      )}

      {/* Aspect ratio — image, gallery, products */}
      {['single_image', 'gallery', 'products'].includes(block.type) && (
        <div>
          <Label className="text-[#9CA3AF] text-[10.5px] uppercase tracking-wider font-bold block mb-2">Image aspect ratio</Label>
          <div className="grid grid-cols-3 gap-1.5">
            {ASPECTS.map((o) => (
              <button
                key={o.v}
                onClick={() => set({ aspect_ratio: o.v })}
                className={`px-2 py-1.5 rounded-md text-[10.5px] uppercase tracking-wider font-bold border transition-all ${
                  (layout.aspect_ratio || 'auto') === o.v
                    ? 'bg-[#A78BFA]/15 border-[#A78BFA]/50 text-[#A78BFA]'
                    : 'bg-white/[0.02] border-white/10 text-[#9CA3AF] hover:text-white'
                }`}
                data-testid={`layout-aspect-${o.v}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Hero-specific — height + parallax */}
      {block.type === 'hero' && (
        <>
          <div>
            <Label className="text-[#9CA3AF] text-[10.5px] uppercase tracking-wider font-bold block mb-2">Hero height</Label>
            <div className="grid grid-cols-4 gap-1.5">
              {[
                { v: 'compact', label: '55vh' },
                { v: 'medium',  label: '75vh' },
                { v: 'tall',    label: '85vh' },
                { v: 'full',    label: '100vh' },
              ].map((o) => (
                <button
                  key={o.v}
                  onClick={() => set({ hero_height: o.v })}
                  className={`px-2 py-1.5 rounded-md text-[10px] uppercase tracking-wider font-bold border transition-all ${
                    (layout.hero_height || 'tall') === o.v
                      ? 'bg-[#A78BFA]/15 border-[#A78BFA]/50 text-[#A78BFA]'
                      : 'bg-white/[0.02] border-white/10 text-[#9CA3AF] hover:text-white'
                  }`}
                  data-testid={`layout-hero-height-${o.v}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!layout.parallax}
              onChange={(e) => set({ parallax: e.target.checked })}
              className="mt-1 w-4 h-4 accent-[#A78BFA]"
              data-testid="layout-parallax"
            />
            <div>
              <p className="text-white text-[12px] font-bold">Parallax background</p>
              <p className="text-[#9CA3AF] text-[10.5px]">Background stays fixed as page scrolls</p>
            </div>
          </label>
        </>
      )}
    </div>
  );
}

function BackgroundBlock({ block, setStyle }) {
  const s = block?.content?._style || {};
  return (
    <div className="space-y-3">
      <Label className="text-[#9CA3AF] text-[10.5px] uppercase tracking-wider font-bold block">Background Color</Label>
      <div className="flex flex-wrap gap-1.5">
        {COLOR_PRESETS.map(c2 => (
          <button
            key={c2}
            onClick={() => setStyle('bgColor', c2)}
            className={`w-6 h-6 rounded-md border-2 transition-all ${s.bgColor === c2 ? 'border-[#8B5CF6] scale-110' : 'border-white/10 hover:border-white/30'}`}
            style={{ backgroundColor: c2 }}
            data-testid={`bg-color-${c2}`}
            aria-label={`Set background color to ${c2}`}
          />
        ))}
        <input type="color" value={s.bgColor || '#ffffff'} onChange={(e) => setStyle('bgColor', e.target.value)} className="w-6 h-6 rounded-md cursor-pointer border-0" title="Custom color" />
      </div>
      <Label className="text-[#9CA3AF] text-[10.5px] uppercase tracking-wider font-bold block mt-2">Background Image</Label>
      <Input
        value={s.bgImage || ''}
        onChange={(e) => setStyle('bgImage', e.target.value)}
        placeholder="Paste image URL or use Background panel above"
        className="bg-white/5 border-white/10 text-white rounded-md text-xs"
        data-testid="bg-image-url"
      />
      <p className="text-[10.5px] text-[#64748B] leading-snug">Hint — Double-click any image on the canvas to open the image toolbar with crop, overlay and replace.</p>
    </div>
  );
}

function SpacingBlock({ block, setMeta, setStyle }) {
  const c = block.content || {};
  return (
    <div className="space-y-4">
      <div>
        <Label className="text-[#9CA3AF] text-[10.5px] uppercase tracking-wider font-bold block">Section Height</Label>
        <div className="flex gap-1.5 mt-2">
          {HEIGHTS.map(h => (
            <button
              key={h.v}
              type="button"
              onClick={() => setMeta('section_height', h.v)}
              className={`flex-1 px-2 py-1.5 rounded-md text-[10px] uppercase tracking-wider font-bold border transition-all ${(c.section_height || 'normal') === h.v
                ? 'bg-[#8B5CF6]/20 text-[#A78BFA] border-[#8B5CF6]/30'
                : 'bg-white/5 text-[#64748B] border-white/5 hover:border-white/10 hover:text-white'}`}
              data-testid={`spacing-height-${h.v}`}
            >
              {h.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <Label className="text-[#9CA3AF] text-[10.5px] uppercase tracking-wider font-bold">Attach to section above</Label>
        <input
          type="checkbox"
          checked={!!c.attached}
          onChange={(e) => setMeta('attached', e.target.checked)}
          data-testid="spacing-attached"
        />
      </div>
      <p className="text-[10.5px] text-[#64748B] leading-snug">Removes the gap with the previous section so they visually flow as one.</p>
    </div>
  );
}

function AnimationBlock({ style, setStyle }) {
  const kind = style.animation || 'none';
  const delay = style.animationDelay || 0;
  return (
    <div className="space-y-3">
      <Label className="text-[#9CA3AF] text-[10.5px] uppercase tracking-wider font-bold block">Entrance Animation</Label>
      <div className="grid grid-cols-3 gap-1.5">
        {ANIMATIONS.map(a => (
          <button
            key={a.v}
            type="button"
            onClick={() => setStyle('animation', a.v)}
            className={`px-2 py-1.5 rounded-md text-[10px] uppercase tracking-wider font-bold border transition-all ${kind === a.v
              ? 'bg-[#8B5CF6]/20 text-[#A78BFA] border-[#8B5CF6]/30'
              : 'bg-white/5 text-[#64748B] border-white/5 hover:border-white/10 hover:text-white'}`}
            data-testid={`anim-${a.v}`}
          >
            {a.label}
          </button>
        ))}
      </div>
      <div>
        <Label className="text-[#9CA3AF] text-[10.5px] uppercase tracking-wider font-bold block">Delay (ms)</Label>
        <Input
          type="number"
          value={delay}
          step={50}
          min={0}
          onChange={(e) => setStyle('animationDelay', Number(e.target.value))}
          className="bg-white/5 border-white/10 text-white rounded-md text-xs mt-1"
          data-testid="anim-delay"
        />
      </div>
    </div>
  );
}

function VisibilityBlock({ block, onUpdate }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-[#9CA3AF] text-[10.5px] uppercase tracking-wider font-bold">Show this section</Label>
        <input
          type="checkbox"
          checked={block.is_visible !== false}
          onChange={(e) => onUpdate(block.id, '__visibility__', e.target.checked)}
          data-testid="visibility-toggle"
        />
      </div>
      <p className="text-[10.5px] text-[#64748B] leading-snug">Hidden sections appear dimmed in the editor but won&rsquo;t render on your published site.</p>
    </div>
  );
}

function SeoBlock({ c, setMeta }) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-[#9CA3AF] text-[10.5px] uppercase tracking-wider font-bold block">Section Anchor ID</Label>
        <Input
          value={c.anchor_id || ''}
          onChange={(e) => setMeta('anchor_id', e.target.value.replace(/[^a-z0-9\-_]/gi, '-').toLowerCase())}
          placeholder="about, contact, pricing…"
          className="bg-white/5 border-white/10 text-white rounded-md text-xs mt-1"
          data-testid="seo-anchor"
        />
        <p className="text-[10.5px] text-[#64748B] leading-snug mt-1">
          Use as <code className="text-[#A78BFA]">#your-anchor</code> in nav links to jump directly to this section.
        </p>
      </div>
    </div>
  );
}

function AdvancedBlock({ c, setMeta }) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-[#9CA3AF] text-[10.5px] uppercase tracking-wider font-bold block">Custom CSS Class</Label>
        <Input
          value={c.custom_class || ''}
          onChange={(e) => setMeta('custom_class', e.target.value)}
          placeholder="my-hero special-promo"
          className="bg-white/5 border-white/10 text-white rounded-md text-xs mt-1"
          data-testid="adv-class"
        />
        <p className="text-[10.5px] text-[#64748B] leading-snug mt-1">Space-separated class names. Combine with Site Settings → Custom CSS.</p>
      </div>
    </div>
  );
}
